import { Card, CardContent, CardTitle } from "@/shared/ui/card";
import { Badge } from "@/shared/ui/badge";
import { ArrowRightLeft, MoreVertical, Pencil, Plus, SatelliteDish, Trash } from "lucide-react";
import { cn } from "@/shared/utils/date-utils";
import { UserTerminal } from "@/shared/types/userTerminalTypes";
import { ServiceLinesSection } from "@/domains/service-lines/components/ServiceLinesSection";
import { useMemo } from "react";
import Dropdown from "@/shared/components/Dropdown";
import { ServiceLine } from "@/shared/types/serviceLineTypes";
import { getTerminalTypeBGColorClass, TERMINAL_TYPE_INFO } from "@/shared/utils/color-utils-terminals";

interface UserTerminalCardProps {
    terminal: UserTerminal;
    onManageTerminal?: () => void;
    onAddTerminalToOrganization?: () => void;
    onRemoveTerminalFromOrganization?: () => void;
    onRemovedTerminalFromStarlink?: () => void;
    onTransferTerminalToOrganization?: () => void;
    onManageServiceLine?: (serviceLine: ServiceLine) => void;
}

const UserTerminalCard = ({
    terminal,
    onManageTerminal,
    onAddTerminalToOrganization,
    onRemoveTerminalFromOrganization,
    onRemovedTerminalFromStarlink,
    onTransferTerminalToOrganization,
    onManageServiceLine,
}: UserTerminalCardProps) => {
    const dropdownMenuItems = useMemo(() => {
        const items = [];
        if (onManageTerminal) {
            items.push({
                label: 'Manage',
                icon: Pencil,
                variant: 'default',
                onClick: onManageTerminal,
            });
        }
        if (onAddTerminalToOrganization && terminal.organization === undefined) {
            items.push({
                label: 'Add to Organization',
                icon: Plus,
                variant: 'default',
                onClick: onAddTerminalToOrganization,
            });
        }
        if (onRemoveTerminalFromOrganization && onTransferTerminalToOrganization && terminal.organization && terminal.serviceLine?.serviceLineNumber === undefined) {
            items.push({
                label: 'Remove from Organization',
                icon: Trash,
                variant: 'destructive',
                onClick: onRemoveTerminalFromOrganization,
            }, {
                label: 'Transfer Terminal',
                icon: ArrowRightLeft,
                variant: 'default',
                onClick: onTransferTerminalToOrganization,
            });
        }
        if (onRemovedTerminalFromStarlink && terminal.organization === undefined) {
            items.push({
                label: 'Remove from Starlink',
                icon: Trash,
                variant: 'destructive',
                onClick: onRemovedTerminalFromStarlink,
            });
        }
        return items;
    }, [terminal, onManageTerminal, onAddTerminalToOrganization, onRemoveTerminalFromOrganization, onRemovedTerminalFromStarlink, onTransferTerminalToOrganization]);

    return (
        <Card className="flex flex-col gap-4">
            <CardContent className="flex flex-col gap-4 p-4">
                {/* Header Section */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 rounded-lg flex items-center justify-center border">
                            <SatelliteDish className="h-6 w-6" />
                        </div>
                        <div>
                            <div className="flex items-center space-x-2">
                                <CardTitle className="text-lg">{terminal.kitSerialNumber}</CardTitle>
                            </div>
                            <div className="flex items-center gap-2">
                                <Badge variant="default">ID: {terminal.userTerminalId}</Badge>
                                <Badge variant="default">Serial: {terminal.dishSerialNumber}</Badge>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {terminal.type && (
                            <Badge
                                variant="default"
                                className={cn('text-xs border', getTerminalTypeBGColorClass(terminal.type))}
                            >
                                {terminal.type ? TERMINAL_TYPE_INFO[terminal.type].value : 'Not Set'}
                            </Badge>
                        )}
                        {/* Action Buttons */}
                        {dropdownMenuItems.length > 0 && (
                            <Dropdown triggerIcon={MoreVertical} items={dropdownMenuItems} />
                        )}
                    </div>
                </div>
                <div className="space-y-2">
                    {terminal.serviceLine ? (
                        <ServiceLinesSection serviceLines={terminal.serviceLine ? [terminal.serviceLine] : undefined} onManageServiceLine={onManageServiceLine} />
                    ) : (
                        <p className="text-sm italic text-red-500">No service lines associated with this terminal.</p>
                    )}
                </div>
            </CardContent>
        </Card>
    );
};

export default UserTerminalCard; 